import { Document, Page } from "react-pdf";
import { Fragment, MutableRefObject, useEffect, useRef, useState } from "react";

import { Loading } from "./base/Loading";
import { PDF_DOCUMENT_OPTIONS } from "./pdfOptions";
import range from "lodash/range";

interface FileSplitterProps {
  pageNumberBeforeSplit: number;
  selected: boolean;
  onClick: (selected: boolean, pageNumberBeforeSplit: number) => void;
}

const FileSplitter = (props: FileSplitterProps) => {
  const extendingLineClassName = `tw-border-0 tw-border-t ${props.selected ? "tw-border-solid tw-border-red-1" : "tw-border-dotted tw-border-gray-5"} tw-flex-grow`;

  return (
    <div className="tw-flex tw-items-center tw-justify-center tw-pt-4">
      <div className="tw-flex tw-w-full tw-max-w-3xl tw-items-center">
        <div className={extendingLineClassName}></div>
        <button
          className={`tw-border-[1px] tw-border-solid ${props.selected ? "tw-border-red-1 tw-bg-red-1 tw-text-white" : "tw-border-gray-5 tw-bg-gray-light-3 tw-text-gray-5 hover:tw-border-gray-1 hover:tw-bg-gray-light-2 hover:tw-text-gray-1"} tw-w-[140px] tw-rounded-[38px]`}
          onClick={() =>
            props.onClick(!props.selected, props.pageNumberBeforeSplit)
          }
        >
          <i className="fas fa-scissors tw-mr-2" />
          <span>Split</span>
        </button>
        <div className={extendingLineClassName}></div>
      </div>
    </div>
  );
};

interface Props {
  file: string | File;
  pageWidth?: number;
  rotation?: number;
  setRotation?: (newRotation: number) => void;
  setPdfError?: (error: boolean) => void;
  splitPages?: number[];
  setSplitPages?: (splitPages: number[]) => void;
  pageRefs?: MutableRefObject<HTMLDivElement[]>;
}

export function PdfViewer(props: Props) {
  const [pageCount, setPageCount] = useState<number>(0);
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const prevFileRef = useRef(props.file);

  // Reset state when the file changes to avoid rendering stale Page
  // components for page numbers that don't exist in the new document.
  useEffect(() => {
    if (props.file !== prevFileRef.current) {
      prevFileRef.current = props.file;
      setPageCount(0);
      setIsPasswordProtected(false);
    }
  }, [props.file]);

  if (isPasswordProtected) {
    return (
      <div className="tw-p-6 tw-text-center tw-text-gray-3">
        <i className="fas fa-lock tw-mb-2 tw-text-[24px]" />
        <p>
          PDF is password protected. Please delete and request a new one from
          the clinic.
        </p>
      </div>
    );
  }

  return (
    <Document
      className="tw-m-0"
      file={props.file}
      options={PDF_DOCUMENT_OPTIONS}
      loading={<Loading className="tw-my-[20px]" size={48} />}
      noData="No PDF specified"
      error="Couldn't load pdf"
      onLoadSuccess={(pdf: any) => {
        pdf.getPage(1).then((page: any) => {
          setPageCount(pdf.numPages);
          if (props.setRotation) props.setRotation(page.rotate);
        });
      }}
      onLoadError={(e: Error) => {
        console.error("Failed to load PDF:", e);
        if (props.setPdfError) {
          props.setPdfError(true);
        }
      }}
      onPassword={() => {
        setIsPasswordProtected(true);
      }}
    >
      {range(1, pageCount + 1).map((index) => (
        <Fragment key={index}>
          <Page
            key={index}
            inputRef={(el: any) => {
              if (el && props.pageRefs) {
                props.pageRefs.current[index] = el;
              }
            }}
            pageNumber={index}
            renderTextLayer={false} // https://github.com/wojtekmaj/react-pdf/issues/1292
            renderAnnotationLayer={false} // if true, renders a blank page
            rotate={props.rotation}
            width={props.pageWidth || 600}
            className={`!tw-bg-transparent tw-p-[20px] tw-drop-shadow-[0_1px_8px_rgba(0,0,0,0.25)] ${index !== pageCount ? "tw-pb-0" : ""}`}
          />
          {props.splitPages && props.setSplitPages && index !== pageCount && (
            <FileSplitter
              selected={props.splitPages.includes(index)}
              pageNumberBeforeSplit={index}
              onClick={(selected, pageNumberBeforeSplit) => {
                if (props.splitPages && props.setSplitPages && selected) {
                  props.setSplitPages([
                    ...props.splitPages,
                    pageNumberBeforeSplit,
                  ]);
                } else if (
                  props.splitPages &&
                  props.setSplitPages &&
                  !selected
                ) {
                  props.setSplitPages(
                    props.splitPages.filter(
                      (page) => page !== pageNumberBeforeSplit,
                    ),
                  );
                }
              }}
            />
          )}
        </Fragment>
      ))}
    </Document>
  );
}
